import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Check, ArrowDown } from "lucide-react";
import { MaskLine } from "./Reveal";
import { scrollToId as scrollTo } from "@/lib/scroll";

const HERO_BG = "https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const INCLUDES = ["Professionelle Website", "Domain inklusive", "Hosting inklusive", "Betreuung inklusive"];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const scaleBg = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative min-h-[92vh] flex flex-col justify-end overflow-hidden pt-28" data-testid="hero">
      <motion.div style={{ y: yBg, scale: scaleBg }} className="absolute inset-0 -z-10">
        <img src={HERO_BG} alt="" className="h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0705] via-[#0a0705]/80 to-[#0a0705]/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0705] via-transparent to-transparent" />
      </motion.div>

      <motion.div style={{ opacity }} className="mx-auto w-full max-w-[1400px] px-6 lg:px-10 pb-12">
        <div className="overflow-hidden mb-7">
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="flex flex-wrap items-center gap-x-3 gap-y-2 font-tech text-xs uppercase tracking-[0.25em] text-[#FF3B00]"
          >
            <span className="h-px w-10 bg-[#FF3B00]" />
            Website · Domain · Hosting · Betreuung
          </motion.div>
        </div>

        <h1 className="font-display text-[12vw] sm:text-6xl lg:text-8xl leading-[0.92] tracking-tight text-white uppercase">
          <MaskLine delay={0.15}>Ihre professionelle</MaskLine>
          <MaskLine delay={0.3} className="text-outline">Website —</MaskLine>
          <MaskLine delay={0.45}>
            ab 49 €<span className="text-[#FF3B00]">/Monat.</span>
          </MaskLine>
        </h1>

        <div className="mt-9 grid lg:grid-cols-[1fr_auto] gap-8 lg:gap-14 items-end">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="max-w-xl"
          >
            <p className="text-base sm:text-lg text-neutral-300 leading-relaxed">
              Wir erstellen, hosten und betreuen Ihren professionellen Webauftritt – inklusive Domain
              und technischer Betreuung. <span className="text-white">Ohne hohe Anfangsinvestition.</span>
            </p>
            <ul className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3">
              {INCLUDES.map((t) => (
                <li key={t} className="flex items-center gap-2.5 text-neutral-200 text-sm">
                  <Check size={16} className="text-[#FF3B00] shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
              <button
                onClick={() => scrollTo("#kontakt")}
                className="group inline-flex items-center justify-center gap-2 bg-[#FF3B00] text-white font-tech text-sm uppercase tracking-[0.12em] px-7 py-4 hover:bg-[#FF5C2B] active:scale-95 transition-all duration-200"
                data-testid="hero-cta-primary"
              >
                Jetzt Website anfragen
                <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </button>
              <button
                onClick={() => scrollTo("#preise")}
                className="inline-flex items-center justify-center gap-2 border border-white/20 text-white font-tech text-sm uppercase tracking-[0.12em] px-7 py-4 hover:border-[#FF3B00] hover:text-[#FF3B00] transition-colors duration-300"
                data-testid="hero-cta-secondary"
              >
                Pakete ansehen
              </button>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1 }}
          className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-white/10 pt-6 text-neutral-400"
        >
          <p className="text-sm">
            <span className="text-white font-medium">12 Monate Mindestlaufzeit</span> – danach monatlich flexibel.
          </p>
          <div className="ml-auto hidden lg:flex items-center gap-2 text-neutral-500 font-tech text-xs uppercase tracking-[0.2em] animate-pulse">
            Scroll <ArrowDown size={14} />
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
